/**
 * ShareModal
 *
 * Modal for generating a survey URL and sharing to social/email with
 * clipboard helpers and namespace-aware links.
 */
import React, { useState } from 'react';
import { X, Share2, Facebook, Twitter, Linkedin as LinkedIn, Instagram, ExternalLink, Mail, Check } from 'lucide-react';
import { CopyButton } from '../../ui/buttons/copy-button';

export function ShareModal({ isOpen, onClose, campaignId, campaignName, namespaceId, campaignDescription }) {
  if (!isOpen) return null;
  
  // State for copy success message
  const [copySuccess, setCopySuccess] = useState(false);

  // Create survey URL - include namespace ID if available
  const surveyUrl = namespaceId 
    ? `${window.location.origin}/survey/${campaignId}?ns=${namespaceId}`
    : `${window.location.origin}/survey/${campaignId}`;

  // Create a clean description for sharing (remove HTML tags if any)
  const cleanDescription = campaignDescription
    ? campaignDescription.replace(/<[^>]*>/g, '')
    : '';
    
  // Use a shortened description for sharing
  const shortenedDescription = cleanDescription.length > 100
    ? cleanDescription.substring(0, 97) + '...'
    : cleanDescription;
    
  // Function to copy the sharing text to clipboard
  const copyToClipboard = (platform) => {
    const title = campaignName || 'Survey';
    const text = shortenedDescription || `Check out this survey: ${campaignName}`;
    let clipboardText = '';
    
    switch(platform) {
      case 'instagram':
        clipboardText = `${title}\n\n${text}\n\n${surveyUrl}`;
        break;
      default:
        clipboardText = `${text} ${surveyUrl}`;
    }
    
    navigator.clipboard.writeText(clipboardText).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    }).catch(err => {
      console.error('Failed to copy text: ', err);
    });
  };

  const shareOnSocial = (platform) => {
    const title = encodeURIComponent(campaignName || 'Survey');
    const text = encodeURIComponent(shortenedDescription || `Check out this survey: ${campaignName}`);
    const url = encodeURIComponent(surveyUrl);
    
    let shareUrl;
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`;
        break;
      case 'twitter': // Now X, but keeping Twitter in the UI for recognition
        shareUrl = `https://twitter.com/intent/tweet?text=${title}&url=${url}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}&title=${title}&summary=${text}`;
        break;
      case 'instagram':
        // For Instagram, we'll copy to clipboard and notify the user
        copyToClipboard('instagram');
        return;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${text}%20${url}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${title}&body=${text}%0A%0A${url}`;
        break;
      default:
        return;
    }

    // For most platforms, open in a new window
    if (platform === 'email') {
      window.location.href = shareUrl;
    } else {
      window.open(shareUrl, '_blank', 'width=600,height=500');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-0">
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      <div className="relative w-full max-w-lg transform overflow-hidden rounded-lg bg-white shadow-xl transition-all dark:bg-gray-900 sm:my-8">
        <div className="px-4 pb-4 pt-5 sm:p-6">
          <div className="absolute right-4 top-4">
            <button
              onClick={onClose}
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div>
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/50">
              <Share2 className="h-6 w-6 text-primary-text-600 dark:text-primary-text-400" />
            </div>
            <div className="mt-3 text-center sm:mt-5">
              <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-white">
                Share Campaign
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Share this survey link with your audience to start collecting responses.
                </p>
              </div>
            </div>
          </div>
          
          {copySuccess && (
            <div className="mt-4 rounded-md bg-green-50 p-3 dark:bg-green-900/30">
              <div className="flex items-center">
                <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
                <p className="ml-2 text-sm text-green-800 dark:text-green-300">
                  Content copied to clipboard!
                </p>
              </div>
            </div>
          )}

          <div className="mt-6">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Survey URL
            </label>
            <div className="mt-2 flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  readOnly
                  value={surveyUrl}
                  className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                />
              </div>
              <CopyButton text={surveyUrl} />
            </div>
          </div>

          <div className="mt-6">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Share on Social Media
            </label>
            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
              <button
                onClick={() => shareOnSocial('facebook')}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <Facebook className="h-4 w-4" />
                Facebook
              </button>
              <button
                onClick={() => shareOnSocial('twitter')}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <Twitter className="h-4 w-4" />
                Twitter
              </button>
              <button
                onClick={() => shareOnSocial('linkedin')}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <LinkedIn className="h-4 w-4" />
                LinkedIn
              </button>
              <button
                onClick={() => shareOnSocial('whatsapp')}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp
              </button>
              <button
                onClick={() => shareOnSocial('email')}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <Mail className="h-4 w-4" />
                Email
              </button>
              <button
                onClick={() => shareOnSocial('instagram')}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <Instagram className="h-4 w-4" />
                Instagram
              </button>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <a
                  href={surveyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  <ExternalLink className="h-4 w-4" />
                  Preview Survey
                </a>
              </div>
              <button
                onClick={onClose}
                className="flex-1 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}